import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  styled,
  Box,
  Typography,
  IconButton,
  Divider,
  Stack,
  InputAdornment
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';

import { CustomButton } from '../../../../components/CustomButton';
import { CustomInput } from '../../../../components/CustomInput';
import { ReactComponent as CloseIcon } from '../../../../assets/images/close.svg';
import { ReactComponent as ShowPassIcon } from '../../../../assets/images/showPass.svg';
import { ReactComponent as ShowPassOffIcon } from '../../../../assets/images/showPassOff.svg';
import { changeUserPassword } from '../../../../api/user';

const StyledDialog = styled(Dialog)(() => ({
  '& > .MuiDialog-container': {
    '& > .MuiDialog-paper': {
      position: 'relative',
      paddingTop: '24px',
      paddingBottom: '24px',
      width: '500px'
    }
  }
}));

const validationSchema = yup.object({
  oldPassword: yup
    .string()
    .min(8, 'Пароль должен состоять не менее чем из 8 символов')
    .required('Пароль обязателен'),
  password: yup
    .string()
    .min(8, 'Пароль должен состоять не менее чем из 8 символов')
    .required('Пароль обязателен'),
  passwordConfirmation: yup
    .string()
    .required('Подтверждение пароля обязательно')
    .oneOf([yup.ref('password'), null], 'Пароль и поле подтверждения пароля должны совпадать')
});

export const ChangePasswordDialog = ({ opened, modalClose, handleSecondModal }) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isPasswordConfirmationVisible, setIsPasswordConfirmationVisible] = useState(false);

  const {
    handleSubmit,
    values,
    handleChange,
    errors,
    isValidating,
    isSubmitting,
    resetForm,
    handleBlur,
    isValid,
    dirty,
  } = useFormik({
    initialValues: {},
    validationSchema: validationSchema,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: (values) => {
      if (isSubmitting) {
        const { oldPassword, password, passwordConfirmation } = values;
        const payload = {
          old_password: oldPassword,
          password1: password,
          password2: passwordConfirmation,
        };
        changeUserPassword(payload)
          .then((res) => {
            if (res.status) {
              handleSecondModal();
              modalClose();
              resetForm();
            }
          })
          .catch((err) => console.log(err));
      }
    }
  });

  return (
    <StyledDialog open={opened} onClose={modalClose}>
      <IconButton
        aria-label="close"
        onClick={modalClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 18,
          color: (theme) => theme.palette.grey[600]
        }}>
        <CloseIcon />
      </IconButton>
      <Typography
        component="h2"
        variant="h2"
        align="left"
        sx={{
          marginBottom: '24px',
          marginLeft: '24px'
        }}
        data-testid="Title"
      >
        Сменить пароль
      </Typography>
      <Divider />
      <form onSubmit={handleSubmit}>
        <Box
          sx={{
            margin: '0 24px 24px 24px'
          }}>
          {!isValidating && (
            <Box mt="24px" data-testid="ErrorMessage">
              {errors.oldPassword && (
                <Typography component="p" variant="subtitle2" color="error">
                  {errors.oldPassword}
                </Typography>
              )}

              {errors.password && (
                <Typography component="p" variant="subtitle2" color="error">
                  {errors.password}
                </Typography>
              )}

              {errors.passwordConfirmation && (
                <Typography component="p" variant="subtitle2" color="error">
                  {errors.passwordConfirmation}
                </Typography>
              )}
            </Box>
          )}
          <Stack
            spacing={2}
            sx={{
              marginTop: '24px'
            }}>
            <CustomInput
              name="oldPassword"
              value={values.oldPassword}
              placeholder="Старый пароль"
              onChange={handleChange}
              onBlur={handleBlur}
              variant="outlined"
              sx={{ width: 452 }}
              size="small"
              error={Boolean(errors.oldPassword)}
              type={isPasswordVisible ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setIsPasswordVisible(!isPasswordVisible)}>
                      {isPasswordVisible ? <ShowPassOffIcon /> : <ShowPassIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <CustomInput
              name="password"
              value={values.password}
              placeholder="Новый пароль"
              onChange={handleChange}
              onBlur={handleBlur}
              variant="outlined"
              sx={{ width: 452 }}
              size="small"
              error={Boolean(errors.password)}
              type={isPasswordVisible ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setIsPasswordVisible(!isPasswordVisible)}>
                      {isPasswordVisible ? <ShowPassOffIcon /> : <ShowPassIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <CustomInput
              name="passwordConfirmation"
              value={values.passwordConfirmation}
              placeholder="Подтвердите пароль"
              onChange={handleChange}
              onBlur={handleBlur}
              variant="outlined"
              sx={{ width: 452 }}
              size="small"
              type={isPasswordConfirmationVisible ? 'text' : 'password'}
              error={Boolean(errors.passwordConfirmation)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() =>
                        setIsPasswordConfirmationVisible(!isPasswordConfirmationVisible)
                      }>
                      {isPasswordConfirmationVisible ? <ShowPassOffIcon /> : <ShowPassIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Stack>
        </Box>
        <Box
          component="ul"
          sx={{
            width: '100%',
            maxWidth: '452px'
          }}>
          <Typography variant="body" component="li">
            Пароль должен состоять не менее чем из 8 символов.
          </Typography>
          <Typography variant="body" component="li">
            Включать цифры и буквы, по крайней мере, одна из которых должна быть в верхнем регистре.
          </Typography>
          <Typography variant="body" component="li">
            Может содержать символы.
          </Typography>
        </Box>

        <Box
          sx={{
            marginRight: '24px',
            display: 'flex',
            justifyContent: 'end'
          }}>
          <CustomButton type="submit" variant="contained" disabled={!dirty || !isValid}>
            Подтвердить
          </CustomButton>
        </Box>
      </form>
    </StyledDialog>
  );
};

ChangePasswordDialog.propTypes = {
  opened: PropTypes.bool.isRequired,
  modalClose: PropTypes.func,
  handleSecondModal: PropTypes.func
};
